import React,{useEffect,useMemo,useState}from"react";
import{Box,Paper,Typography,TextField,Grid,Table,TableBody,TableCell,TableContainer,TableHead,TableRow,useMediaQuery,MenuItem,ToggleButton,ToggleButtonGroup,Chip}from"@mui/material";
import{Bar,BarChart,CartesianGrid,Cell,Line,LineChart,Pie,PieChart,ResponsiveContainer,Tooltip,XAxis,YAxis}from"recharts";
import Sidebar from"./AdminPanel/Sidebar";
import TopBar from"./AdminPanel/TopBar";
import{useDashboardTheme}from"../../context/DashboardThemeContext";
import{useAlert}from"../../context/AlertContext";

const MESES=[
  {value:1,label:"Enero"},
  {value:2,label:"Febrero"},
  {value:3,label:"Marzo"},
  {value:4,label:"Abril"},
  {value:5,label:"Mayo"},
  {value:6,label:"Junio"},
  {value:7,label:"Julio"},
  {value:8,label:"Agosto"},
  {value:9,label:"Septiembre"},
  {value:10,label:"Octubre"},
  {value:11,label:"Noviembre"},
  {value:12,label:"Diciembre"}
];

function formatMoney(value){
  return Number(value||0).toLocaleString("es-EC",{minimumFractionDigits:2,maximumFractionDigits:2});
}

function formatNumber(value){
  return Number(value||0).toLocaleString("es-EC",{maximumFractionDigits:2});
}

function formatChartDate(value){
  const parts=String(value||"").split("-");
  if(parts.length!==3)return value;
  return`${parts[2]}/${parts[1]}`;
}

function normalizeOthers(items=[]){
  return items.map(item=>({...item,nombre:item.es_varios||String(item.nombre||"").toLowerCase()==="varios"?"Otros":item.nombre}));
}

function splitProductName(value,maxCharacters=18){
  const words=String(value||"").trim().split(/\s+/);
  const lines=[];
  let currentLine="";
  words.forEach(word=>{
    const nextLine=currentLine?`${currentLine} ${word}`:word;
    if(nextLine.length<=maxCharacters){
      currentLine=nextLine;
    }else{
      if(currentLine)lines.push(currentLine);
      currentLine=word;
    }
  });
  if(currentLine)lines.push(currentLine);
  return lines;
}

function isMobileLabelWidth(){
  return typeof window!=="undefined"&&window.innerWidth<=900;
}

function ProductLabel({cx,cy,midAngle,outerRadius,nombre,cantidad,fill}){
  const RADIAN=Math.PI/180;
  const radius=outerRadius+24;
  const x=cx+radius*Math.cos(-midAngle*RADIAN);
  const y=cy+radius*Math.sin(-midAngle*RADIAN);
  const lines=splitProductName(nombre,isMobileLabelWidth()?13:18);
  const textAnchor=x>cx?"start":"end";
  const lineHeight=12;
  const totalLines=lines.length+1;
  const startY=y-((totalLines-1)*lineHeight)/2;
  return(
    <text x={x} y={startY} fill={fill} textAnchor={textAnchor} dominantBaseline="central" fontSize={11} fontWeight={600}>
      {lines.map((line,index)=><tspan key={`${line}-${index}`} x={x} dy={index===0?0:lineHeight}>{line}</tspan>)}
      <tspan x={x} dy={lineHeight}>{formatNumber(cantidad)}</tspan>
    </text>
  );
}

function CustomTooltip({active,payload,label}){
  if(!active||!payload||!payload.length)return null;
  const title=label||payload[0]?.payload?.nombre||payload[0]?.payload?.forma_pago||"";
  return(
    <Paper elevation={0} sx={{p:1.2,borderRadius:"12px",border:"1px solid rgba(148,163,184,0.35)",bgcolor:"#FFFFFF"}}>
      {title&&<Typography sx={{fontSize:12,fontWeight:700,color:"#111827",mb:.4}}>{title}</Typography>}
      {payload.map((item,index)=>(
        <Typography key={`${item.dataKey}-${index}`} sx={{fontSize:12,fontWeight:600,color:"#374151"}}>
          {item.name||item.dataKey}: {item.dataKey==="total"||item.dataKey==="totalFacturado"||item.dataKey==="totalCaja"?`$${formatMoney(item.value)}`:formatNumber(item.value)}
        </Typography>
      ))}
    </Paper>
  );
}

function StatCard({title,value,dashboardTheme}){
  return(
    <Paper elevation={0} sx={{p:{xs:1.8,md:2.2},borderRadius:"20px",border:`1px solid ${dashboardTheme.border_color}`,bgcolor:dashboardTheme.card_background,minHeight:112,display:"flex",flexDirection:"column",justifyContent:"center",boxShadow:"none"}}>
      <Typography sx={{fontSize:13,fontWeight:600,color:dashboardTheme.muted_text_color,mb:.8,lineHeight:1.25}}>{title}</Typography>
      <Typography sx={{fontSize:{xs:22,md:25},fontWeight:700,color:dashboardTheme.text_color,lineHeight:1.15}}>{value}</Typography>
    </Paper>
  );
}

function ChartCard({title,children,dashboardTheme,height=280}){
  return(
    <Paper elevation={0} sx={{p:{xs:1.6,md:2.2},borderRadius:"22px",border:`1px solid ${dashboardTheme.border_color}`,bgcolor:dashboardTheme.card_background,minHeight:height+78,boxShadow:"none"}}>
      <Typography sx={{fontSize:{xs:17,md:19},fontWeight:700,color:dashboardTheme.text_color,mb:2,lineHeight:1.2}}>{title}</Typography>
      <Box sx={{width:"100%",height}}>{children}</Box>
    </Paper>
  );
}

function getWeeksOfMonth(year,month){
  const lastDay=new Date(year,month,0).getDate();
  const monthName=MESES.find(item=>item.value===month)?.label?.toLowerCase()||"";
  const result=[];
  let week=1;
  for(let start=1;start<=lastDay;start+=7){
    const end=Math.min(start+6,lastDay);
    result.push({
      value:week,
      label:`Semana ${week}`,
      range:`${String(start).padStart(2,"0")} - ${String(end).padStart(2,"0")} ${monthName}`
    });
    week+=1;
  }
  return result;
}

export default function AdminStatistics(){
  const[sidebarOpen,setSidebarOpen]=useState(true);
  const{dashboardTheme}=useDashboardTheme();
  const{showError}=useAlert();
  const isMobile=useMediaQuery("(max-width:900px)");
  const currentDate=new Date();
  const currentYear=currentDate.getFullYear();
  const currentMonth=currentDate.getMonth()+1;
  const currentWeek=Math.min(5,Math.floor((currentDate.getDate()-1)/7)+1);
  const[periodo,setPeriodo]=useState("mes");
  const[anio,setAnio]=useState(currentYear);
  const[mes,setMes]=useState(currentMonth);
  const[semana,setSemana]=useState(currentWeek);
  const[loading,setLoading]=useState(false);
  const[data,setData]=useState(null);
  const token=localStorage.getItem("accessToken");
  const chartColor=dashboardTheme.primary_color;
  const secondaryColor=dashboardTheme.theme_mode==="dark"?"rgba(255,255,255,0.68)":"#64748B";
  const gridColor=dashboardTheme.theme_mode==="dark"?"rgba(255,255,255,0.10)":"rgba(15,23,42,0.10)";

  const colors=useMemo(()=>[
    dashboardTheme.primary_color,
    dashboardTheme.button_color,
    dashboardTheme.table_header_color,
    dashboardTheme.sidebar_color,
    dashboardTheme.theme_mode==="dark"?"#94A3B8":"#475569"
  ],[dashboardTheme]);

  const years=useMemo(()=>{
    const result=[];
    for(let year=currentYear;year>=2025;year--)result.push(year);
    return result;
  },[currentYear]);

  const semanas=useMemo(()=>getWeeksOfMonth(anio,mes),[anio,mes]);

  useEffect(()=>{
    if(semana>semanas.length)setSemana(semanas.length);
  },[semanas,semana]);

  const loadStatistics=async()=>{
    try{
      setLoading(true);
      const params=new URLSearchParams();
      params.append("periodo",periodo);
      params.append("anio",String(anio));
      params.append("mes",String(mes));
      if(periodo==="semana")params.append("semana",String(semana));
      const response=await fetch(`/api/statistics/dashboard/?${params.toString()}`,{headers:{Authorization:`Bearer ${token}`}});
      const result=await response.json();
      if(!response.ok){
        showError(result.detail||result.error||"No se pudieron cargar las estadísticas");
        return;
      }
      setData(result);
    }catch{
      showError("Error de conexión");
    }finally{
      setLoading(false);
    }
  };

  useEffect(()=>{
    const delay=setTimeout(()=>loadStatistics(),250);
    return()=>clearTimeout(delay);
  },[periodo,anio,mes,semana]);

  const resumen=data?.resumen||{};
  const productosMasVendidos=useMemo(()=>normalizeOthers(data?.productos_mas_vendidos||[]),[data]);
  const saboresMasVendidos=useMemo(()=>normalizeOthers(data?.sabores_mas_vendidos||[]),[data]);

  const ventasComparadas=useMemo(()=>{
    const map=new Map();
    (data?.facturas_por_dia||[]).forEach(item=>{
      map.set(item.fecha,{fecha:item.fecha,fechaLabel:formatChartDate(item.fecha),totalFacturado:Number(item.total||0),totalCaja:0});
    });
    (data?.caja_por_dia||[]).forEach(item=>{
      const current=map.get(item.fecha)||{fecha:item.fecha,fechaLabel:formatChartDate(item.fecha),totalFacturado:0,totalCaja:0};
      current.totalCaja=Number(item.total||0);
      map.set(item.fecha,current);
    });
    return Array.from(map.values()).sort((a,b)=>a.fecha.localeCompare(b.fecha));
  },[data]);

  const semanaSeleccionada=semanas.find(item=>item.value===semana);
  const periodoLabel=periodo==="semana"
    ?`${semanaSeleccionada?.label||""} · ${semanaSeleccionada?.range||""}`
    :MESES.find(item=>item.value===mes)?.label||"";

  const tableCellSx={
    color:dashboardTheme.text_color,
    borderBottom:`1px solid ${dashboardTheme.border_color}`,
    fontSize:13.5,
    fontWeight:500
  };

  const emptyBox=(
    <Box sx={{height:"100%",minHeight:180,display:"flex",alignItems:"center",justifyContent:"center",color:dashboardTheme.muted_text_color,fontSize:14,fontWeight:600,textAlign:"center"}}>
      {loading?"Cargando estadísticas...":"Sin datos para mostrar"}
    </Box>
  );

  return(
    <Box sx={{minHeight:"100vh",bgcolor:dashboardTheme.dashboard_background,display:"flex",color:dashboardTheme.text_color,overflowX:"hidden"}}>
      <Sidebar open={sidebarOpen} onClose={()=>setSidebarOpen(false)}/>
      <Box sx={{flex:1,minWidth:0,minHeight:"100vh",display:"flex",flexDirection:"column"}}>
        <TopBar sidebarOpen={sidebarOpen} onToggleSidebar={()=>setSidebarOpen(prev=>!prev)}/>
        <Box component="main" sx={{flex:1,width:"100%",p:{xs:1.5,sm:2,md:3}}}>
          <Box sx={{width:{xs:"100%",lg:"95%"},mx:"auto",mt:{xs:1,md:1.5}}}>
            <Box sx={{display:"flex",alignItems:{xs:"stretch",md:"center"},justifyContent:"space-between",flexDirection:{xs:"column",md:"row"},gap:1.5,mb:1.5}}>
              <Box>
                <Typography sx={{fontSize:{xs:24,sm:26,md:30},fontWeight:600,color:dashboardTheme.primary_color,lineHeight:1.2}}>Estadísticas</Typography>
                <Typography sx={{fontSize:13.5,fontWeight:500,color:dashboardTheme.muted_text_color,mt:.5}}>Analiza ventas, caja, productos y formas de pago por mes o por semana.</Typography>
              </Box>
              <Chip label={periodoLabel} sx={{alignSelf:{xs:"flex-start",md:"center"},fontWeight:700,bgcolor:`${dashboardTheme.primary_color}14`,color:dashboardTheme.primary_color,border:`1px solid ${dashboardTheme.primary_color}35`}}/>
            </Box>
            <Paper elevation={0} sx={{mb:1.5,p:{xs:1.5,sm:2},borderRadius:"20px",border:`1px solid ${dashboardTheme.border_color}`,bgcolor:dashboardTheme.card_background,boxShadow:"none"}}>
              <Box sx={{display:"flex",alignItems:{xs:"stretch",md:"center"},justifyContent:"space-between",flexDirection:{xs:"column",md:"row"},gap:1.5}}>
                <ToggleButtonGroup exclusive value={periodo} onChange={(_event,value)=>value&&setPeriodo(value)} size="small" sx={{alignSelf:{xs:"stretch",sm:"flex-start"},"& .MuiToggleButton-root":{px:2.4,py:.85,textTransform:"none",fontWeight:700,color:dashboardTheme.muted_text_color,borderColor:dashboardTheme.border_color},"& .Mui-selected":{color:`${dashboardTheme.primary_color}!important`,bgcolor:`${dashboardTheme.primary_color}12!important`}}}>
                  <ToggleButton value="mes">Por mes</ToggleButton>
                  <ToggleButton value="semana">Por semana</ToggleButton>
                </ToggleButtonGroup>
                <Box sx={{display:"flex",flexDirection:{xs:"column",sm:"row"},gap:1,width:{xs:"100%",md:"auto"}}}>
                  <TextField select size="small" label="Año" value={anio} onChange={event=>setAnio(Number(event.target.value))} sx={{width:{xs:"100%",sm:125},...dashboardTheme.inputStyles}}>
                    {years.map(year=><MenuItem key={year} value={year}>{year}</MenuItem>)}
                  </TextField>
                  <TextField select size="small" label="Mes" value={mes} onChange={event=>{setMes(Number(event.target.value));setSemana(1);}} sx={{width:{xs:"100%",sm:165},...dashboardTheme.inputStyles}}>
                    {MESES.map(item=><MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}
                  </TextField>
                  {periodo==="semana"&&(
                    <TextField select size="small" label="Semana" value={semana} onChange={event=>setSemana(Number(event.target.value))} sx={{width:{xs:"100%",sm:250},...dashboardTheme.inputStyles}}>
                      {semanas.map(item=><MenuItem key={item.value} value={item.value}>{item.label} · {item.range}</MenuItem>)}
                    </TextField>
                  )}
                </Box>
              </Box>
            </Paper>
            <Grid container spacing={1.5} sx={{mb:1.5}}>
              <Grid item xs={12} sm={6} md={3}><StatCard title="Total facturado" value={`$${formatMoney(resumen.total_facturado)}`} dashboardTheme={dashboardTheme}/></Grid>
              <Grid item xs={12} sm={6} md={3}><StatCard title="Total en caja" value={`$${formatMoney(resumen.total_caja_ventas)}`} dashboardTheme={dashboardTheme}/></Grid>
              <Grid item xs={12} sm={6} md={3}><StatCard title="Efectivo" value={`$${formatMoney(resumen.total_efectivo)}`} dashboardTheme={dashboardTheme}/></Grid>
              <Grid item xs={12} sm={6} md={3}><StatCard title="Transferencia" value={`$${formatMoney(resumen.total_transferencia)}`} dashboardTheme={dashboardTheme}/></Grid>
              <Grid item xs={12} sm={6} md={3}><StatCard title="Órdenes cobradas" value={formatNumber(resumen.ordenes_cobradas)} dashboardTheme={dashboardTheme}/></Grid>
              <Grid item xs={12} sm={6} md={3}><StatCard title="Órdenes facturadas" value={formatNumber(resumen.ordenes_facturadas)} dashboardTheme={dashboardTheme}/></Grid>
              <Grid item xs={12} sm={6} md={3}><StatCard title="Cajas abiertas" value={formatNumber(resumen.cajas_abiertas)} dashboardTheme={dashboardTheme}/></Grid>
              <Grid item xs={12} sm={6} md={3}><StatCard title="Diferencia factura/caja" value={`$${formatMoney(resumen.diferencia_facturado_caja)}`} dashboardTheme={dashboardTheme}/></Grid>
            </Grid>
            <Grid container spacing={1.5}>
              <Grid item xs={12} lg={8}>
                <ChartCard title="Facturación y caja por día" dashboardTheme={dashboardTheme} height={320}>
                  {ventasComparadas.length>0?(
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={ventasComparadas}>
                        <CartesianGrid stroke={gridColor} vertical={false}/>
                        <XAxis dataKey="fechaLabel" tick={{fill:secondaryColor,fontSize:12}} axisLine={{stroke:gridColor}} tickLine={{stroke:gridColor}} minTickGap={isMobile?28:18}/>
                        <YAxis tick={{fill:secondaryColor,fontSize:12}} axisLine={{stroke:gridColor}} tickLine={{stroke:gridColor}}/>
                        <Tooltip content={<CustomTooltip/>}/>
                        <Line type="monotone" dataKey="totalFacturado" name="Facturado" stroke={chartColor} strokeWidth={3} dot={{r:3}} activeDot={{r:5}}/>
                        <Line type="monotone" dataKey="totalCaja" name="Caja" stroke={secondaryColor} strokeWidth={3} dot={{r:3}} activeDot={{r:5}}/>
                      </LineChart>
                    </ResponsiveContainer>
                  ):emptyBox}
                </ChartCard>
              </Grid>
              <Grid item xs={12} lg={4}>
                <ChartCard title="Formas de pago" dashboardTheme={dashboardTheme} height={320}>
                  {(data?.pagos_por_forma||[]).length>0?(
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Tooltip content={<CustomTooltip/>}/>
                        <Pie data={data.pagos_por_forma} dataKey="total" nameKey="forma_pago" innerRadius={isMobile?54:66} outerRadius={isMobile?92:112} paddingAngle={3}>
                          {data.pagos_por_forma.map((entry,index)=><Cell key={entry.forma_pago} fill={colors[index%colors.length]}/>)}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                  ):emptyBox}
                </ChartCard>
              </Grid>
              <Grid item xs={12} lg={6}>
                <ChartCard title="Productos más vendidos" dashboardTheme={dashboardTheme} height={320}>
                  {productosMasVendidos.length>0?(
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart margin={{top:20,right:isMobile?70:100,bottom:20,left:isMobile?70:100}}>
                        <Tooltip content={<CustomTooltip/>}/>
                        <Pie data={productosMasVendidos} dataKey="cantidad" nameKey="nombre" outerRadius={isMobile?74:98} paddingAngle={2} label={props=><ProductLabel {...props} fill={secondaryColor}/>} labelLine={{stroke:secondaryColor}}>
                          {productosMasVendidos.map((entry,index)=><Cell key={`${entry.id||"otros"}-${index}`} fill={colors[index%colors.length]}/>)}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                  ):emptyBox}
                </ChartCard>
              </Grid>
              <Grid item xs={12} lg={6}>
                <ChartCard title="Sabores más vendidos" dashboardTheme={dashboardTheme} height={320}>
                  {saboresMasVendidos.length>0?(
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={saboresMasVendidos} margin={{top:10,right:15,left:0,bottom:isMobile?65:35}}>
                        <CartesianGrid stroke={gridColor} vertical={false}/>
                        <XAxis dataKey="nombre" interval={0} angle={isMobile?-45:0} textAnchor={isMobile?"end":"middle"} height={isMobile?85:50} tick={{fill:secondaryColor,fontSize:12}} axisLine={{stroke:gridColor}} tickLine={{stroke:gridColor}}/>
                        <YAxis tick={{fill:secondaryColor,fontSize:12}} axisLine={{stroke:gridColor}} tickLine={{stroke:gridColor}}/>
                        <Tooltip content={<CustomTooltip/>}/>
                        <Bar dataKey="cantidad" name="Cantidad" radius={[10,10,0,0]}>
                          {saboresMasVendidos.map((entry,index)=><Cell key={`${entry.id||"otros"}-${index}`} fill={colors[index%colors.length]}/>)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ):emptyBox}
                </ChartCard>
              </Grid>
              <Grid item xs={12}>
                <Paper elevation={0} sx={{borderRadius:"22px",border:`1px solid ${dashboardTheme.border_color}`,bgcolor:dashboardTheme.card_background,overflow:"hidden",boxShadow:"none"}}>
                  <Box sx={{px:{xs:1.6,md:2.2},py:1.8,borderBottom:`1px solid ${dashboardTheme.border_color}`}}>
                    <Typography sx={{fontSize:{xs:17,md:19},fontWeight:700,color:dashboardTheme.text_color,lineHeight:1.2}}>Resumen del período</Typography>
                    <Typography sx={{fontSize:12.5,fontWeight:500,color:dashboardTheme.muted_text_color,mt:.4}}>{periodoLabel}</Typography>
                  </Box>
                  <TableContainer sx={{width:"100%",overflowX:"auto"}}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{bgcolor:dashboardTheme.table_header_color}}>
                          <TableCell sx={dashboardTheme.tableStyles.headCell}>Concepto</TableCell>
                          <TableCell align="right" sx={dashboardTheme.tableStyles.headCell}>Valor</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={dashboardTheme.tableStyles.bodyRow}>
                          <TableCell sx={tableCellSx}>Facturas emitidas</TableCell>
                          <TableCell align="right" sx={tableCellSx}>{formatNumber(resumen.facturas)}</TableCell>
                        </TableRow>
                        <TableRow sx={dashboardTheme.tableStyles.bodyRow}>
                          <TableCell sx={tableCellSx}>Órdenes</TableCell>
                          <TableCell align="right" sx={tableCellSx}>{formatNumber(resumen.ordenes)}</TableCell>
                        </TableRow>
                        <TableRow sx={dashboardTheme.tableStyles.bodyRow}>
                          <TableCell sx={tableCellSx}>Pagos registrados</TableCell>
                          <TableCell align="right" sx={tableCellSx}>{formatNumber(resumen.pagos)}</TableCell>
                        </TableRow>
                        <TableRow sx={dashboardTheme.tableStyles.bodyRow}>
                          <TableCell sx={tableCellSx}>Ingresos manuales</TableCell>
                          <TableCell align="right" sx={tableCellSx}>${formatMoney(resumen.total_ingresos_manuales)}</TableCell>
                        </TableRow>
                        <TableRow sx={dashboardTheme.tableStyles.bodyRow}>
                          <TableCell sx={tableCellSx}>Egresos manuales</TableCell>
                          <TableCell align="right" sx={tableCellSx}>${formatMoney(resumen.total_egresos_manuales)}</TableCell>
                        </TableRow>
                        <TableRow sx={dashboardTheme.tableStyles.bodyRow}>
                          <TableCell sx={tableCellSx}>Cajas cerradas</TableCell>
                          <TableCell align="right" sx={tableCellSx}>{formatNumber(resumen.cajas_cerradas)}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}